/**
 * First-time hints (docs/ux.md §15a.2): which mechanics have come up in the player's own game
 * right now. Read from the position and the legal actions, never from a scripted order, so a
 * hint shows whenever its moment first comes, in whatever game.
 */
import { HINT_KEYS, type HintKey } from '@brig/api-types';
import { reachableCells, targetsInRange, type Action, type GameState, type Side } from '@brig/rules-engine';
import type { Topic } from './topics.ts';

export { HINT_KEYS, type HintKey };

/** The "How to play" page each hint's "More…" opens. */
export const HINT_TOPIC: Readonly<Record<HintKey, Topic>> = {
  muster: 'muster',
  wind: 'wind',
  becalmed: 'movement',
  storm: 'storm',
  fire: 'fire',
  volley: 'fire',
  damaged: 'ports',
  dock: 'ports',
  fort: 'forts',
  royalPort: 'royal',
};

/**
 * The hints whose moment is now for `side`, the player at this screen, in table order (U-LEARN-06).
 * `phase` is the turn's UI phase: hints only come in the player's own turn, while they choose
 * the squadron or command it.
 */
export function dueHints(
  state: GameState,
  legal: readonly Action[],
  side: Side,
  phase: 'muster' | 'battle',
): HintKey[] {
  if (state.status.kind !== 'playing' || state.turn.side !== side) return [];
  const spin = state.turn.spin;
  const own = state.ships.filter((s) => s.side === side && !s.sunk);
  const due: Record<HintKey, () => boolean> = {
    muster: () => phase === 'muster' && spin === null,
    wind: () => phase === 'battle' && spin !== null,
    // A ship of the squadron the wind left without a cell to go to: calm, or a headwind.
    becalmed: () =>
      phase === 'battle' &&
      spin !== null &&
      spin.weather !== 'storm' &&
      state.turn.group.some((id) => {
        const ship = own.find((s) => s.id === id);
        return ship !== undefined && !ship.moved && reachableCells(state, id).length === 0;
      }),
    storm: () => spin?.weather === 'storm',
    fire: () => legal.some((a) => a.type === 'fire'),
    volley: () => legal.some((a) => a.type === 'volley'),
    damaged: () => own.some((s) => s.damage > 0),
    dock: () => legal.some((a) => a.type === 'enterPortRepair'),
    fort: () =>
      state.forts.some(
        (f) =>
          f.side !== side &&
          !f.destroyed &&
          targetsInRange(state, f.id).some((id) => own.some((s) => s.id === id)),
      ),
    royalPort: () => {
      const ports = new Set(Object.values(state.map.royalPorts));
      return state.ships.some((s) => !s.sunk && ports.has(s.cell));
    },
  };
  return HINT_KEYS.filter((key) => due[key]());
}

/** The one hint to show now: the first due one not seen yet (U-LEARN-07: one at a time). */
export function nextHint(due: readonly HintKey[], seen: ReadonlySet<string>): HintKey | null {
  return due.find((key) => !seen.has(key)) ?? null;
}
